<template>
  <div class="flex flex-col">
    <Popover
      v-if="collapsed"
      data-testid="sidebar-group-popover"
      :clickable="false"
      :hide-delay="150"
      hoverable
      :offset="4"
      placement="right-start"
      :show-delay="0"
    >
      <template #trigger="{ active: isFlyoutOpen }">
        <NavMenuGroupHeader
          :name="text"
          :icon="icon"
          :active="active"
          :collapsed="collapsed"
          :controls="panelId"
          :expanded="isFlyoutOpen"
          @click="emit('toggleMenu', text)"
        />
      </template>
      <template #panel>
        <div
          :id="panelId"
          class="w-[177px] overflow-hidden rounded-[8px] bg-[--v3-theme-primarycolor] text-[--v3-theme-primarytextcolor] shadow-[0px_12px_16px_-4px_rgba(0,0,0,0.08),0px_4px_6px_-2px_rgba(0,0,0,0.03)]"
        >
          <div class="border-b border-[--color-brand-forrest-green-80] p-[10px] text-[12px] font-medium leading-[15px] text-[--color-brand-forrest-green-20]">
            {{ text }}
          </div>
          <div class="flex flex-col gap-px p-[8px]">
            <slot />
          </div>
        </div>
      </template>
    </Popover>
    <NavMenuGroupHeader
      v-else
      :name="text"
      :icon="icon"
      :active="active"
      :collapsed="collapsed"
      :controls="panelId"
      :expanded="isExpanded"
      @click="emit('toggleMenu', text)"
    />
    <div
      v-if="!collapsed"
      :id="panelId"
      ref="collapsablePanel"
      class="nav-menu-group-container duration grid w-[277px] transition-all"
      :class="{
        active: isExpanded,
        collapsed,
      }"
    >
      <div class="w-full overflow-y-hidden">
        <slot />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, provide, ref, useId, watchEffect } from 'vue';

import Popover from '@/common/components/atoms/Popover';
import NavMenuGroupHeader from '@/modules/layout/components/atoms/NavMenuGroupHeader';
import { MenuGroupItemsCounterFn } from '@/modules/layout/types/keys';

const props = withDefaults(defineProps<{
  text: string,
  icon: string,
  active?: boolean,
  collapsed?: boolean,
  expanded?: boolean,
  routes?: unknown[],
}>(), {
  active: false,
  collapsed: false,
  expanded: undefined,
  routes: () => [],
});

const emit = defineEmits<{
  toggleMenu: [value: string];
}>();

const panelId = useId();
const itemsCount = ref<number>(0);
const increaseGroupItemCounter = () => {
  itemsCount.value++;
};
provide(MenuGroupItemsCounterFn, increaseGroupItemCounter);

const collapsablePanel = ref(null);
const collapsablePanelHeight = computed(() => (props.routes.length || itemsCount.value) * 41);
const isExpanded = computed(() => props.expanded ?? props.active);

watchEffect(() => {
  if (!collapsablePanel.value) return;
  if (props.collapsed) {
    (collapsablePanel.value as HTMLElement).style.height = '0';
  } else if (isExpanded.value) {
    (collapsablePanel.value as HTMLElement).style.height = `${collapsablePanelHeight.value}px`;
  } else {
    (collapsablePanel.value as HTMLElement).style.height = '0';
  }
});
</script>

<style lang="scss" scoped>
.nav-menu-group-container.collapsed {
  width: 50px;
}
</style>
